import type { AiCallDimension } from "@pax/api-contract/ai/calls/v1alpha1";

export type Dimension = AiCallDimension;

/** For each dimension, the values a call must have one of. */
export type Keep = Partial<Record<Dimension, string[]>>;

/** Which calls a question is about, before its stretch of time. */
export interface CallFilter {
  keep: Keep;
  promptOver?: number;
}

export const dimensionGroups: { label: string; dimensions: Dimension[] }[] = [
  { label: "What ran", dimensions: ["lab", "model"] },
  {
    label: "How it got there",
    dimensions: ["router", "transport", "host", "inferenceLocation", "sku"],
  },
  { label: "Who asked", dimensions: ["agent", "agentVersion", "purpose", "gameKind"] },
  { label: "Who paid", dimensions: ["payer"] },
  { label: "About the call", dimensions: ["serviceTier", "outcome", "fault", "errorType"] },
];

export const dimensions: Record<Dimension, { label: string; plural: string }> = {
  lab: { label: "lab", plural: "Labs" },
  model: { label: "model", plural: "Models" },
  router: { label: "router", plural: "Routers" },
  transport: { label: "transport", plural: "Transports" },
  host: { label: "host", plural: "Hosts" },
  inferenceLocation: { label: "inference location", plural: "Inference locations" },
  sku: { label: "SKU", plural: "SKUs" },
  agent: { label: "agent", plural: "Agents" },
  agentVersion: { label: "agent version", plural: "Agent versions" },
  payer: { label: "payer", plural: "Payers" },
  purpose: { label: "purpose", plural: "Purposes" },
  gameKind: { label: "games or evals", plural: "Games or evals" },
  serviceTier: { label: "tier", plural: "Service tiers" },
  outcome: { label: "outcome", plural: "Outcomes" },
  fault: { label: "fault", plural: "Faults" },
  errorType: { label: "error type", plural: "Error types" },
};

export const NO_ERROR = "none";

export const faultNames: Record<string, string> = {
  upstream: "Provider",
  request: "Our request",
  pax: "Pax",
  model: "Model output",
  cancelled: "Cancelled",
  none: "No error",
};

export const promptSizes: [tokens: number, name: string][] = [
  [32_000, "over 32k"],
  [128_000, "over 128k"],
  [200_000, "over 200k"],
];

const fixedNames: Partial<Record<Dimension, Record<string, string>>> = {
  lab: {
    anthropic: "Anthropic",
    openai: "OpenAI",
    google: "Google",
    moonshotai: "Moonshot AI",
    deepseek: "DeepSeek",
    "x-ai": "xAI",
    "z-ai": "Z.ai",
    qwen: "Qwen",
  },
  router: { direct: "Direct", openrouter: "OpenRouter" },
  transport: {
    "alibaba-beijing-messages": "Alibaba Beijing · Messages",
    "alibaba-singapore-messages": "Alibaba Singapore · Messages",
    "alibaba-virginia-messages": "Alibaba Virginia · Messages",
    "anthropic-messages": "Anthropic · Messages",
    "openai-responses": "OpenAI · Responses",
    "openrouter-chat-completions": "OpenRouter · Chat",
    "openrouter-responses": "OpenRouter · Responses",
    "vertex-generate-content": "Vertex · generateContent",
  },
  host: {
    alibaba: "Alibaba Cloud",
    anthropic: "Anthropic",
    openai: "OpenAI",
    vertex: "Google Vertex",
    "google-vertex": "Google Vertex",
    "google-ai-studio": "Google AI Studio",
    deepinfra: "DeepInfra",
  },
  inferenceLocation: {
    none: "Not reported",
    global: "Global",
    us: "US only",
    singapore: "Singapore",
    virginia: "Virginia",
    beijing: "Beijing",
  },
  sku: { unknown: "No SKU" },
  agent: { none: "No agent" },
  agentVersion: { none: "No agent" },
  payer: { none: "Nobody" },
  purpose: {
    game_master: "Game Master",
    player_agent: "Player agent",
    grader: "Grader",
    client: "Signed-in user",
  },
  gameKind: { play: "Games", eval: "Evals", none: "Outside a game" },
  serviceTier: { none: "Default" },
  outcome: { succeeded: "Succeeded", malformed: "Malformed", failed: "Failed" },
  fault: faultNames,
  errorType: { none: "No error" },
};

/** Display names the API sent for values that are ids, such as agents, kept as they arrive. */
const sentNames = new Map<string, string>();

export function rememberNames(names: Record<string, string>) {
  for (const [id, name] of Object.entries(names)) sentNames.set(id, name);
}

const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export function nameOf(dimension: Dimension, value: string): string {
  const sent = sentNames.get(value);
  if (sent) return sent;
  const fixed = fixedNames[dimension]?.[value];
  if (fixed) return fixed;
  if (dimension === "serviceTier" || dimension === "lab") return capitalized(value);
  if (dimension === "host") return value.split("-").map(capitalized).join(" ");
  if (dimension === "agent" || dimension === "agentVersion" || dimension === "payer") {
    return value.slice(0, 8);
  }
  return value;
}

const markFile = (file: string) => `/images/models/${file}`;

/** Marks by a provider's name with case and punctuation dropped, so `Moonshot AI` is `moonshotai`. */
const marks: Record<string, string> = {
  anthropic: markFile("lab-anthropic.png"),
  openai: markFile("lab-openai.png"),
  google: markFile("lab-google.png"),
  googleaistudio: markFile("lab-google.png"),
  vertex: markFile("vertex.png"),
  googlevertex: markFile("vertex.png"),
  moonshotai: markFile("lab-moonshotai.png"),
  qwen: markFile("lab-qwen.png"),
  zai: markFile("lab-z-ai.png"),
  moonshot: markFile("lab-moonshotai.png"),
  chutes: markFile("chutes.png"),
  friendli: markFile("friendli.png"),
  infron: markFile("infron.png"),
  openrouter: markFile("openrouter.webp"),
};

const markOf = (name: string) => marks[name.toLowerCase().replace(/[^a-z0-9]/g, "")];

/** The lab behind a model, from an OpenRouter-style `lab/model` name or the family's prefix. */
function labOfModel(model: string): string | undefined {
  const [prefix, rest] = model.split("/");
  if (rest && prefix) return prefix;
  if (model.startsWith("claude")) return "anthropic";
  if (/^(gpt|o\d|codex)/.test(model)) return "openai";
  if (model.startsWith("gemini")) return "google";
  if (model.startsWith("kimi")) return "moonshotai";
  if (model.startsWith("qwen")) return "qwen";
  if (model.startsWith("glm")) return "z-ai";
  return undefined;
}

/** The mark beside a value: the lab for what ran, the router or host for how it got there. */
export function iconOf(dimension: Dimension, value: string): string | undefined {
  if (dimension === "lab" || dimension === "host" || dimension === "router") return markOf(value);
  if (dimension === "model") {
    const lab = labOfModel(value);
    return lab ? markOf(lab) : undefined;
  }
  if (dimension === "transport") return markOf(value.split("-")[0] ?? "");
  return undefined;
}

/** One quiet line under a value's name, where there is something worth saying. */
export function aboutOf(dimension: Dimension, value: string): string | undefined {
  if (dimension === "model") {
    const lab = labOfModel(value);
    return lab ? nameOf("lab", lab) : undefined;
  }
  return undefined;
}

/** A model belongs to one lab and a transport to one router, so pinning either pins its parent too. */
const parentOf: Partial<Record<Dimension, Dimension>> = { model: "lab", transport: "router" };

export const pinned = (dimension: Dimension, keep: Keep) =>
  keep[dimension]?.length === 1 ||
  (Object.entries(parentOf) as [Dimension, Dimension][]).some(
    ([child, parent]) => parent === dimension && keep[child]?.length === 1,
  );

export const isDimension = (value: string): value is Dimension => value in dimensions;
